import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { BarChart3, Check, Cloud, Cookie, ShieldCheck, X } from 'lucide-react'
import { Link } from 'react-router-dom'

import Button from '@components/ui/Button'
import { usePrivacyPreferences } from '@contexts/PrivacyPreferencesContext'
import { panelTransition } from '@utils/animations'
import { OVERLAY_IDS } from '@utils/overlayIds'

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

function focusPrivacySettingsLauncher() {
  window.requestAnimationFrame(() => {
    document.querySelector('button[aria-label="Privacy settings"]')?.focus()
  })
}

function PreferenceSwitch({
  id,
  title,
  description,
  checked,
  disabled,
  icon: Icon,
  onChange
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 border-t border-[var(--x3-border)] py-5 first:border-t-0 first:pt-0 last:pb-0">
      <div className="flex min-w-0 gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--x3-surface-subtle)] text-[var(--x3-control-accent)]">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <p
            id={`${id}-label`}
            className="text-sm font-semibold text-[var(--x3-text)]"
          >
            {title}
          </p>
          <p
            id={`${id}-description`}
            className="mt-1 text-sm leading-6 text-[var(--x3-text-muted)]"
          >
            {description}
          </p>
        </div>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-description`}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={`relative mt-1 h-7 w-12 shrink-0 rounded-full border transition-colors ${
          checked
            ? 'border-[#4F46E5] bg-[#4F46E5]'
            : 'border-[var(--x3-border)] bg-[var(--x3-surface-subtle)]'
        } disabled:cursor-not-allowed disabled:opacity-70`}
      >
        <span
          className={`absolute top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-[1.3rem]' : 'translate-x-0.5'
          }`}
        >
          {checked && (
            <Check className="h-3 w-3 text-[#4F46E5]" aria-hidden="true" />
          )}
        </span>
        <span className="sr-only">{checked ? 'Enabled' : 'Disabled'}</span>
      </button>
    </div>
  )
}

function PrivacySettingsDialog() {
  const {
    categories,
    closeSettings,
    globalPrivacyControl,
    isSettingsOpen,
    rejectOptional,
    saveCategories
  } = usePrivacyPreferences()
  const [draft, setDraft] = useState(categories)
  const dialogRef = useRef(null)
  const closeButtonRef = useRef(null)
  const returnFocusRef = useRef(null)
  const reduceMotion = useReducedMotion()

  useEffect(() => () => closeSettings(), [closeSettings])

  useEffect(() => {
    if (!isSettingsOpen) return undefined

    setDraft(categories)
    returnFocusRef.current = document.activeElement
    const previousOverflow = document.body.style.overflow
    const backgroundRegions = Array.from(
      document.querySelectorAll(
        'header, main, footer, [data-release-status], [aria-label="Privacy choices"]'
      )
    ).filter(region => !dialogRef.current?.contains(region))
    const backgroundState = backgroundRegions.map(region => ({
      region,
      hadInert: region.hasAttribute('inert'),
      inertValue: region.getAttribute('inert'),
      ariaHidden: region.getAttribute('aria-hidden')
    }))

    document.body.style.overflow = 'hidden'
    backgroundRegions.forEach(region => {
      region.setAttribute('inert', '')
      region.setAttribute('aria-hidden', 'true')
    })

    const focusFrame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus()
    )
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        closeSettings()
        return
      }

      if (event.key !== 'Tab') return
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) || []
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(focusFrame)
      document.body.style.overflow = previousOverflow
      backgroundState.forEach(
        ({ region, hadInert, inertValue, ariaHidden }) => {
          if (hadInert) {
            region.setAttribute('inert', inertValue ?? '')
          } else {
            region.removeAttribute('inert')
          }

          if (ariaHidden === null) {
            region.removeAttribute('aria-hidden')
          } else {
            region.setAttribute('aria-hidden', ariaHidden)
          }
        }
      )
      document.removeEventListener('keydown', handleKeyDown)
      const returnFocusTarget = returnFocusRef.current
      window.requestAnimationFrame(() => {
        const fallbackTarget = document.querySelector(
          'button[aria-label="Privacy settings"]'
        )
        const openedFromBanner = returnFocusTarget?.closest?.(
          '[aria-label="Privacy choices"]'
        )
        const focusTarget =
          openedFromBanner && fallbackTarget
            ? fallbackTarget
            : returnFocusTarget?.isConnected
              ? returnFocusTarget
              : fallbackTarget
        focusTarget?.focus?.()
      })
    }
  }, [categories, closeSettings, isSettingsOpen])

  return (
    <AnimatePresence>
      {isSettingsOpen && (
        <motion.div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-[#090B0E]/65 p-0 sm:items-center sm:p-6"
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={reduceMotion ? undefined : { opacity: 0 }}
          transition={reduceMotion ? { duration: 0 } : panelTransition}
          onMouseDown={event => {
            if (event.target === event.currentTarget) closeSettings()
          }}
        >
          <motion.section
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="privacy-settings-title"
            aria-describedby="privacy-settings-description"
            className="max-h-[92svh] w-full overflow-y-auto rounded-t-lg border border-[var(--x3-border)] bg-[var(--x3-surface)] shadow-2xl sm:max-w-2xl sm:rounded-lg"
            initial={reduceMotion ? false : { y: 24, opacity: 0.98 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduceMotion ? undefined : { y: 24, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : panelTransition}
          >
            <header className="flex items-start justify-between gap-5 border-b border-[var(--x3-border)] px-5 py-5 sm:px-6">
              <div>
                <p className="font-mono text-xs font-semibold uppercase text-[var(--x3-control-accent)]">
                  Privacy controls
                </p>
                <h2
                  id="privacy-settings-title"
                  className="mt-2 text-xl font-semibold text-[var(--x3-text)]"
                >
                  Choose what this browser can use
                </h2>
                <p
                  id="privacy-settings-description"
                  className="mt-2 text-sm leading-6 text-[var(--x3-text-muted)]"
                >
                  Necessary technologies remain active. Optional choices can be
                  changed at any time.
                </p>
              </div>
              <Button
                ref={closeButtonRef}
                type="button"
                variant="ghost"
                size="icon"
                onClick={closeSettings}
                aria-label="Close privacy settings"
                title="Close"
                className="shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </header>

            <div className="px-5 py-6 sm:px-6">
              <PreferenceSwitch
                id="privacy-necessary"
                title="Necessary"
                description="Protects requests, keeps accounts signed in, remembers this privacy choice, and stores preferences you explicitly request."
                checked
                disabled
                icon={ShieldCheck}
              />
              <PreferenceSwitch
                id="privacy-experience"
                title="Offline and performance experience"
                description="Stores public pages and images on this device so supported content can load faster and remain available offline."
                checked={draft.experience}
                onChange={experience =>
                  setDraft(current => ({ ...current, experience }))
                }
                icon={Cloud}
              />
              <PreferenceSwitch
                id="privacy-analytics"
                title="Analytics and diagnostics"
                description="Allows consented browser error diagnostics and performance measurement so we can investigate failures."
                checked={draft.analytics && !globalPrivacyControl}
                disabled={globalPrivacyControl}
                onChange={analytics =>
                  setDraft(current => ({ ...current, analytics }))
                }
                icon={BarChart3}
              />

              {globalPrivacyControl && (
                <p className="mt-5 border-l-2 border-[#0F9D78] pl-3 text-sm leading-6 text-[var(--x3-text-muted)]">
                  Your browser sends Global Privacy Control, so analytics
                  remains off.
                </p>
              )}
            </div>

            <footer className="flex flex-col gap-3 border-t border-[var(--x3-border)] bg-[var(--x3-surface-subtle)] px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={rejectOptional}
              >
                Reject optional
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={() => saveCategories(draft)}
              >
                Save choices
              </Button>
            </footer>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function ConsentBanner() {
  const {
    acceptAll,
    activeOverlayId,
    globalPrivacyControl,
    hasDecision,
    openSettings,
    rejectOptional
  } = usePrivacyPreferences()
  const reduceMotion = useReducedMotion()
  const isObscuredByAnotherOverlay =
    activeOverlayId && activeOverlayId !== OVERLAY_IDS.PRIVACY_SETTINGS
  const acceptAndMoveFocus = () => {
    acceptAll()
    focusPrivacySettingsLauncher()
  }
  const rejectAndMoveFocus = () => {
    rejectOptional()
    focusPrivacySettingsLauncher()
  }

  return (
    <AnimatePresence>
      {!hasDecision && (
        <motion.aside
          aria-label="Privacy choices"
          aria-hidden={isObscuredByAnotherOverlay ? 'true' : undefined}
          inert={isObscuredByAnotherOverlay ? '' : undefined}
          className="fixed inset-x-0 bottom-0 z-[80] max-h-[100svh] overflow-y-auto border-t border-[var(--x3-border)] bg-[var(--x3-surface)] shadow-[0_-12px_36px_rgba(9,11,14,0.16)] print:hidden"
          initial={reduceMotion ? false : { y: '100%' }}
          animate={{ y: isObscuredByAnotherOverlay ? '100%' : 0 }}
          exit={reduceMotion ? undefined : { y: '100%' }}
          transition={reduceMotion ? { duration: 0 } : panelTransition}
        >
          <div className="mx-auto grid max-w-7xl gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:px-8">
            <div className="flex min-w-0 gap-3 sm:gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--x3-action-soft)] text-[var(--x3-control-accent)]">
                <Cookie className="h-5 w-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-[var(--x3-text)]">
                  Cookies and similar technologies
                </h2>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-[var(--x3-text-muted)]">
                  x3Algo uses necessary storage for security and requested
                  features. With your choice, we can also enable offline content
                  and browser diagnostics. Read the{' '}
                  <Link
                    to="/cookies"
                    className="font-medium text-[var(--x3-link)] underline underline-offset-2"
                  >
                    Cookie and Storage Policy
                  </Link>
                  .
                  {globalPrivacyControl &&
                    ' Your browser has already kept analytics off.'}
                </p>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-3 lg:min-w-[31rem]">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={rejectAndMoveFocus}
                fullWidth
              >
                Reject optional
              </Button>
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={openSettings}
                fullWidth
              >
                Manage choices
              </Button>
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={acceptAndMoveFocus}
                fullWidth
              >
                Accept all
              </Button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  )
}

function PrivacySettingsLauncher() {
  const { activeOverlayId, hasDecision, isSettingsOpen, openSettings } =
    usePrivacyPreferences()
  const reduceMotion = useReducedMotion()

  return (
    <AnimatePresence>
      {hasDecision && !isSettingsOpen && (
        <motion.div
          aria-hidden={activeOverlayId ? 'true' : undefined}
          inert={activeOverlayId ? '' : undefined}
          className="fixed bottom-4 left-4 z-[70] print:hidden"
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{
            opacity: activeOverlayId ? 0 : 1,
            y: activeOverlayId ? 8 : 0
          }}
          exit={reduceMotion ? undefined : { opacity: 0, y: 8 }}
          transition={reduceMotion ? { duration: 0 } : panelTransition}
        >
          <Button
            type="button"
            variant="secondary"
            size="icon"
            onClick={openSettings}
            aria-label="Privacy settings"
            title="Privacy settings"
            className="border border-[var(--x3-border)] bg-[var(--x3-surface)] shadow-lg"
          >
            <Cookie className="h-5 w-5" aria-hidden="true" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function CookieConsent() {
  return (
    <>
      <ConsentBanner />
      <PrivacySettingsLauncher />
      <PrivacySettingsDialog />
    </>
  )
}

export default CookieConsent
